import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  PhoneNumberField,
  RootLayer,
  TextField,
  Link,
  view,
  tokens,
  Select,
  Checkbox,
  InlineFeedback,
} from '@sail/ui';
import {assignProps, assignTokens, toIntent} from '@sail/engine';
import {
  DotComButton,
  dotComColors,
  dotComTransitions,
  useTheme,
} from '@stripe-internal/conversations-ui';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import {
  countries,
  personalDomainsData,
  consentPrecheckedCountries,
  implicitCountries,
} from './constants';
import {
  contactSalesFormViewed,
  contactSalesFormSubmitted,
  contactSalesFormSubmitError,
  contactSalesFormCancelled,
} from './analytics';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  business_country: string;
  additional_info?: string;
  marketing_opt_in?: boolean;
  analytics_event_id?: string;
}

export type ValueProps = {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  country?: string;
  conversation_id?: string;
  additional_info?: string;
  submitted?: boolean;
  error?: string;
  marketing_opt_in?: boolean;
  form_type?: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export const messages = defineMessages({
  firstNameLabel: {
    id: 'mse_inline_data.contact_sales.first_name_label',
    description:
      'Label for the first name field. Shown above the first name input.',
    defaultMessage: 'First name',
  },
  firstNamePlaceholder: {
    id: 'mse_inline_data.contact_sales.first_name_placeholder',
    description: 'Placeholder text for the first name input field.',
    defaultMessage: 'Jane',
  },
  lastNameLabel: {
    id: 'mse_inline_data.contact_sales.last_name_label',
    description:
      'Label for the last name field. Shown above the last name input.',
    defaultMessage: 'Last name',
  },
  lastNamePlaceholder: {
    id: 'mse_inline_data.contact_sales.last_name_placeholder',
    description: 'Placeholder text for the last name input field.',
    defaultMessage: 'Diaz',
  },
  emailLabel: {
    id: 'mse_inline_data.contact_sales.email_label',
    description:
      'Label for the email field. Guides the user to enter a work email.',
    defaultMessage: 'Work email',
  },
  emailPlaceholder: {
    id: 'mse_inline_data.contact_sales.email_placeholder',
    description:
      'Placeholder text for the email field, showing an example email address.',
    defaultMessage: 'jane@example.com',
  },
  phoneLabel: {
    id: 'mse_inline_data.contact_sales.phone_label',
    description: 'Label for the phone number field.',
    defaultMessage: 'Phone number',
  },
  businessCountryLabel: {
    id: 'mse_inline_data.contact_sales.business_country_label',
    description:
      'Label for the country select field. Indicates the country in which the business is located.',
    defaultMessage: 'Country/Region',
  },
  title: {
    id: 'mse_inline_data.contact_sales.title',
    description:
      'Title text that explains what the user should do on the Contact Sales form.',
    defaultMessage: 'Enter your information below',
  },
  infoTextChat: {
    id: 'mse_inline_data.contact_sales.info_text_chat',
    description:
      'Informational text that explains what the user should do on the Contact Sales chat form.',
    defaultMessage:
      'After you submit, a sales representative will join this chat and may also follow up to answer any questions you have.',
  },
  infoTextEmail: {
    id: 'mse_inline_data.contact_sales.info_text_email',
    description:
      'Informational text that explains what the user should do on the Contact Sales email form.',
    defaultMessage: 'After you submit, we’ll follow up as soon as possible.',
  },
  phoneRequired: {
    id: 'mse_inline_data.contact_sales.phone_required',
    description:
      'Error message shown when the phone number field is left blank or invalid.',
    defaultMessage: 'Please fill out a phone number.',
  },
  countryRequired: {
    id: 'mse_inline_data.contact_sales.country_required',
    description:
      'Error message shown when the business country field is not selected.',
    defaultMessage: 'Country/region is required.',
  },
  enterValidEmail: {
    id: 'mse_inline_data.contact_sales.enter_valid_email',
    description:
      'Error message shown when no email is entered or when the field is empty.',
    defaultMessage: 'Please enter a valid email address.',
  },
  invalidEmail: {
    id: 'mse_inline_data.contact_sales.invalid_email',
    description:
      'Error message shown when the user has entered an invalid email format.',
    defaultMessage: 'Invalid Email Address',
  },
  enterValidWorkEmail: {
    id: 'mse_inline_data.contact_sales.enter_valid_work_email',
    description:
      'Error message shown when the email entered appears to be a personal email whereas a work email is required.',
    defaultMessage: 'Please enter a valid work email to continue.',
  },
  cancelButton: {
    id: 'mse_inline_data.contact_sales.cancel_button',
    description:
      'Text for the cancel button. Indicates the action to cancel the form.',
    defaultMessage: 'Cancel',
  },
  submitButton: {
    id: 'mse_inline_data.contact_sales.submit_button',
    description:
      'Text for the submit button. Indicates the action to submit the form.',
    defaultMessage: 'Submit',
  },
  marketingImplicitDescription: {
    id: 'mse_inline_data.contact_sales.marketing_implicit_description',
    description: 'Informational text for implicit marketing communications.',
    defaultMessage:
      'You may receive marketing communications from Stripe including product updates, industry news, and event invitations. You can {unsubscribeLink} at any time.',
  },
  marketingConsentLabel: {
    id: 'mse_inline_data.contact_sales.marketing_consent_label',
    description:
      'Label for the checkbox to get emails about product updates, industry news, and events.',
    defaultMessage:
      'Receive marketing communication from Stripe including product updates, industry news, and event invitations. You can {unsubscribeLink} at any time.',
  },
  unsubscribe: {
    id: 'mse_inline_data.contact_sales.unsubscribe',
    description: 'Text for unsubscribe link',
    defaultMessage: 'unsubscribe',
  },
  submissionError: {
    id: 'mse_inline_data.contact_sales.submission_error',
    description:
      'Error message shown when the form submission fails and the user needs to try again.',
    defaultMessage: 'Submission failed. Please try again.',
  },
});

const font = {
  body: {
    medium: {
      size: '14px',
      lineHeight: '20px',
    },
  },
  label: {
    medium: {size: '14px'},
  },
};

const getHelmTheme = (accentColor = dotComColors.brandButton) => {
  const color = {
    neutral: {
      0: '#ffffff',
      25: '#F6F9FB',
      100: '#E7ECF1',
      400: '#ABB5C5',
      700: '#3F4B66',
      800: '#0A2540',
      900: '#0a2540',
    },
    brand: {
      600: accentColor,
      900: '#0c2e4e',
    },
    text: {
      $default: tokens.color.neutral[700],
      form: {
        placeholder: tokens.color.neutral[400],
      },
    },
  };

  return toIntent([assignTokens(tokens, {font, color})]);
};

const VALID_EMAIL_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

export const isEmailAddressValid = (email: string): boolean => {
  return VALID_EMAIL_REGEX.test(email);
};

export const shouldValidateWorkEmail = (country: string) => {
  if (!country) return true;
  return personalDomainsData.countries.includes(country);
};

export const containsPersonalDomain = (emailAddress: string): boolean => {
  const lowerEmail = emailAddress.toLowerCase().replace(/\s/g, '');
  return personalDomainsData.domains.some((domain) =>
    lowerEmail.includes(domain.toLowerCase()),
  );
};

export const getDefaultMarketingOptIn = (country: string): boolean => {
  if (implicitCountries.includes(country)) {
    return true;
  }
  if (consentPrecheckedCountries.includes(country)) {
    return true;
  }
  return false; // Rest of world: default unchecked
};

const UnsubscribeLink: React.FC = () => (
  <Link
    type="primary"
    href="https://go.stripe.global/subscribe"
    target="_blank"
    rel="noopener noreferrer"
    css={{fontWeight: 'inherit'}}
  >
    <FormattedMessage {...messages.unsubscribe} />
  </Link>
);

export const ContactSalesForm = (props: InlineDatumReactComponentProps) => {
  const {datum, action, onDone, isDone} = props;
  const intl = useIntl();
  const analytics = useAnalytics();
  const loggedViewed = useRef<boolean>(false);
  const theme = useTheme();

  const sortedCountries = useMemo(() => {
    return countries
      .map((country) => ({
        code: country.code,
        name: intl.formatDisplayName(country.code, {type: 'region'}),
      }))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [intl]);

  const {
    submitted,
    error,
    form_type: formType,
    conversation_id: conversationId,
  } = datum.value as ValueProps;

  const initialValue = (datum?.value as ValueProps) || {};

  const initialFormData: FormData = {
    first_name: initialValue.first_name ?? '',
    last_name: initialValue.last_name ?? '',
    email: initialValue.email ?? '',
    phone_number: initialValue.phone_number ?? '',
    business_country: initialValue.country ?? 'US',
    additional_info: initialValue.additional_info ?? '',
    marketing_opt_in: getDefaultMarketingOptIn(initialValue.country ?? 'US'),
    analytics_event_id: '',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    const getAnalyticsEventId = async () => {
      try {
        const analyticsEventId = await analytics.requestAnalyticsEventId();
        setFormData((prev) => ({
          ...prev,
          analytics_event_id: analyticsEventId || '',
        }));
      } catch (err) {
        setFormData((prev) => ({
          ...prev,
          analytics_event_id: '',
        }));
      }
    };
    getAnalyticsEventId();
  }, [analytics]);
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const [pending, setPending] = useState<boolean>(false);

  useEffect(() => {
    if (loggedViewed.current || isDone) {
      return;
    }
    loggedViewed.current = true;
    analytics.viewed(contactSalesFormViewed, {
      streaming_conversation_id: conversationId,
      formType,
    });
  }, [analytics, conversationId, isDone, formType]);

  const [formFrozen, setFormFrozen] = useState<boolean | undefined>(isDone);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);

  const validateField = (
    name: string,
    ref: React.RefObject<HTMLInputElement>,
  ) => {
    const errorMsg =
      ref.current && !ref.current.checkValidity()
        ? ref.current.validationMessage
        : undefined;
    setFormErrors((prevErrors) => ({...prevErrors, [name]: errorMsg}));
  };

  const getEmailErrors = useCallback(
    (value: string, country: string): string | undefined => {
      if (!value) {
        return intl.formatMessage(messages.enterValidEmail);
      } else if (!isEmailAddressValid(value)) {
        return intl.formatMessage(messages.invalidEmail);
      } else if (
        shouldValidateWorkEmail(country) &&
        containsPersonalDomain(value)
      ) {
        return intl.formatMessage(messages.enterValidWorkEmail);
      } else {
        return undefined;
      }
    },
    [intl],
  );

  const validateEmail = () => {
    const emailError = getEmailErrors(
      formData.email,
      formData.business_country,
    );
    setFormErrors((prev) => ({...prev, email: emailError}));
  };

  const validateFields = useCallback((): FormErrors => {
    return {
      first_name:
        firstNameRef.current && !firstNameRef.current.checkValidity()
          ? firstNameRef.current.validationMessage
          : undefined,
      last_name:
        lastNameRef.current && !lastNameRef.current.checkValidity()
          ? lastNameRef.current.validationMessage
          : undefined,
      email: getEmailErrors(formData.email, formData.business_country),
      phone_number: !formData.phone_number
        ? intl.formatMessage(messages.phoneRequired)
        : undefined,
      business_country: !formData.business_country
        ? intl.formatMessage(messages.countryRequired)
        : undefined,
    };
  }, [formData, intl, firstNameRef, lastNameRef, getEmailErrors]);

  const isSubmitDisabled = useMemo(() => {
    const errors = validateFields();
    return Object.values(errors).some((fieldError) => fieldError !== undefined);
  }, [validateFields]);

  const clearErrorOnInteraction = useCallback(() => {
    if (submissionError) {
      setSubmissionError(null);
    }
  }, [submissionError]);

  const validatePhoneNumber = () => {
    const phoneError = !formData.phone_number
      ? intl.formatMessage(messages.phoneRequired)
      : undefined;
    setFormErrors((prev) => ({...prev, phone_number: phoneError}));
  };

  const handlePhoneChange = (fieldValue: string) => {
    clearErrorOnInteraction();
    setFormData((prev) => ({...prev, phone_number: fieldValue}));
    if (fieldValue) {
      setFormErrors((prev) => ({...prev, phone_number: ''}));
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    clearErrorOnInteraction();
    const {name, value} = event.target;
    if (name === 'business_country') {
      const defaultMarketingOptIn = getDefaultMarketingOptIn(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        marketing_opt_in: defaultMarketingOptIn,
      }));
    } else {
      setFormData((prev) => ({...prev, [name]: value}));
    }
    if (name in formErrors) {
      setFormErrors((prevErrors) => ({...prevErrors, [name]: undefined}));
    }
  };

  const handleCancel = () => {
    setFormFrozen(true);
    onDone({
      type: 'success',
      message: 'ContactSalesForm',
      extra: {
        dataWithPii: {...formDataRef.current},
        outcome: 'cancelled',
      },
    });
    analytics.action(contactSalesFormCancelled, {
      streaming_conversation_id: conversationId,
      formType,
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    const hasErrors = Object.values(validateFields()).some(
      (err) => err !== undefined,
    );
    if (!hasErrors) {
      setPending(true);
      setFormFrozen(true);
      const dynamicPayload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phone_number,
        email: formData.email,
        country: formData.business_country,
        additional_info: formData.additional_info,
        marketing_opt_in: formData.marketing_opt_in,
        conversation_id: conversationId,
        analytics_event_id: formData.analytics_event_id,
      };
      action(dynamicPayload);
    }
  };

  useEffect(() => {
    if (error === 'server_error') {
      analytics.action(contactSalesFormSubmitError, {
        streaming_conversation_id: conversationId,
        formType,
      });
      setPending(false);
      onDone({type: 'error'});
      return;
    }

    if (error !== null) {
      analytics.action(contactSalesFormSubmitError, {
        streaming_conversation_id: conversationId,
        formType,
      });
      setPending(false);
      setFormFrozen(false);
      // for now we set an error message inside the form and let the user resubmit
      // TODO: review errors type and potentially show error message from the API here
      setSubmissionError(intl.formatMessage(messages.submissionError));
      return;
    }

    if (submitted === true && !isDone) {
      analytics.action(contactSalesFormSubmitted, {
        streaming_conversation_id: conversationId,
        formType,
      });
      setPending(false);
      onDone({
        type: 'success',
        message: 'ContactSalesForm',
        extra: {
          dataWithPii: {...formDataRef.current},
          outcome: 'submitted',
        },
      });
    }
  }, [
    isDone,
    onDone,
    error,
    submitted,
    conversationId,
    analytics,
    intl,
    formType,
  ]);

  const renderMarketingConsent = useMemo(() => {
    if (implicitCountries.includes(formData.business_country)) {
      return (
        <view.p
          css={{
            margin: 0,
            font: 'label.small',
            marginTop: 'xsmall',
            textColor: tokens.color.neutral[700],
          }}
          data-testid="marketing-implicit-description"
        >
          <FormattedMessage
            {...messages.marketingImplicitDescription}
            values={{
              unsubscribeLink: <UnsubscribeLink />,
            }}
          />
        </view.p>
      );
    }
    return (
      <Checkbox
        label={
          <FormattedMessage
            {...messages.marketingConsentLabel}
            values={{
              unsubscribeLink: <UnsubscribeLink />,
            }}
          />
        }
        checked={!!formData.marketing_opt_in}
        onChange={(e) => {
          clearErrorOnInteraction();
          const isChecked = e.target.checked;
          setFormData((prev) => ({
            ...prev,
            marketing_opt_in: isChecked,
          }));
        }}
        css={{marginTop: 'xsmall'}}
        subviews={{
          label: {
            css: {fontSize: '12px', lineHeight: '1.33', fontWeight: 'normal'},
          },
        }}
        disabled={formFrozen}
        name="marketing_opt_in"
        data-testid="marketing-opt-in-checkbox"
      />
    );
  }, [
    formData.business_country,
    formData.marketing_opt_in,
    formFrozen,
    clearErrorOnInteraction,
  ]);

  const helmTheme = getHelmTheme(theme.linkColor);

  return (
    <RootLayer uses={[helmTheme]}>
      <view.div
        css={{
          backgroundColor: 'offset',
          borderRadius: 'medium',
          padding: 'medium',
          boxShadow: 'top',
          stack: 'y',
          gap: 'medium',
        }}
        data-testid="contact-sales-form"
      >
        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <view.p
            css={{
              font: 'body.medium.emphasized',
              margin: 0,
              textColor: tokens.color.neutral[700],
            }}
          >
            {intl.formatMessage(messages.title)}
          </view.p>
          <view.p
            css={{
              font: 'body.medium',
              margin: 0,
              textColor: tokens.color.neutral[700],
            }}
          >
            {intl.formatMessage(
              formType === 'email'
                ? messages.infoTextEmail
                : messages.infoTextChat,
            )}
          </view.p>
        </view.div>
        <view.form
          onSubmit={handleSubmit}
          noValidate
          css={{stack: 'y', gapY: 'small'}}
        >
          <TextField
            data-testid="first-name-input"
            ref={firstNameRef}
            label={intl.formatMessage(messages.firstNameLabel)}
            name="first_name"
            placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
            required
            value={formData.first_name}
            onChange={handleChange}
            onBlur={() => validateField('first_name', firstNameRef)}
            error={formErrors.first_name}
            disabled={formFrozen}
            subviews={{
              input: {
                css: {
                  paddingTop: 'small',
                  paddingBottom: 'small',
                  borderRadius: 'medium',
                },
              },
            }}
          />
          <TextField
            data-testid="last-name-input"
            ref={lastNameRef}
            label={intl.formatMessage(messages.lastNameLabel)}
            name="last_name"
            placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
            required
            value={formData.last_name}
            onChange={handleChange}
            onBlur={() => validateField('last_name', lastNameRef)}
            error={formErrors.last_name}
            disabled={formFrozen}
            subviews={{
              input: {
                css: {
                  paddingTop: 'small',
                  paddingBottom: 'small',
                  borderRadius: 'medium',
                },
              },
            }}
          />
          <TextField
            data-testid="work-email-input"
            type="email"
            name="email"
            label={intl.formatMessage(messages.emailLabel)}
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            required
            value={formData.email}
            onChange={handleChange}
            onBlur={validateEmail}
            error={formErrors.email}
            disabled={formFrozen}
            subviews={{
              input: {
                css: {
                  paddingTop: 'small',
                  paddingBottom: 'small',
                  borderRadius: 'medium',
                },
              },
            }}
          />
          <PhoneNumberField
            data-testid="phone-number-input"
            name="phone_number"
            label={intl.formatMessage(messages.phoneLabel)}
            value={formData.phone_number}
            onChange={handlePhoneChange}
            error={formErrors.phone_number}
            subviews={{
              inputs: {
                css: {
                  justifyContent: 'normal',
                  paddingTop: 'small',
                  paddingBottom: 'small',
                  borderRadius: 'medium',
                },
              },
              phoneNumberInput: {
                uses: [assignProps({onBlur: validatePhoneNumber})],
              },
            }}
            disabled={formFrozen}
          />
          <Select
            data-testid="business-country-select"
            name="business_country"
            label={intl.formatMessage(messages.businessCountryLabel)}
            value={formData.business_country}
            error={formErrors.business_country}
            onChange={handleChange}
            subviews={{
              input: {
                css: {
                  fontWeight: 'normal',
                  paddingTop: 'small',
                  paddingBottom: 'small',
                  topShadow: 'none',
                  ':hover:not(:disabled)': {
                    topShadow: 'none',
                    keyline: 'form',
                  },
                  borderRadius: 'medium',
                },
              },
            }}
            disabled={formFrozen}
          >
            {sortedCountries.map(({code, name}) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </Select>
          {renderMarketingConsent}

          {submissionError ? (
            <InlineFeedback type="error">{submissionError}</InlineFeedback>
          ) : null}

          <view.div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <view.button
              type="button"
              data-testid="cancel-button"
              onClick={handleCancel}
              disabled={formFrozen || pending}
              css={{
                transition: dotComTransitions.buttonTransition,
                color: dotComColors.brand800,
                border: 'none',
                fontWeight: 'semibold',
                background: 'none',
                ':disabled': {
                  color: '#C4CCD8',
                },
                ':hover:not([disabled])': {
                  color: dotComColors.brand500,
                },
              }}
            >
              {intl.formatMessage(messages.cancelButton)}
            </view.button>
            <DotComButton
              data-testid="submit-button"
              behavior="submit"
              pending={pending}
              // we keep button not disabled during pending state to show the spinner
              disabled={isSubmitDisabled || (formFrozen && !pending)}
              accentColor={theme.linkColor}
            >
              {intl.formatMessage(messages.submitButton)}
            </DotComButton>
          </view.div>
        </view.form>
      </view.div>
    </RootLayer>
  );
};
